import { tw } from "@careinfo/tailwind";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import APIS from "../../../constant/Endpoint";
import useGetHook from "../../../customHooks/useGetHook";
import usePatchHook from "../../../customHooks/usePatchHook";
import DeleteIcon from "../../../Icons/DeleteIcon";
import Button from "../../Reusable/Button";
import Spinner from "../../Reusable/Spinner";

const SortableItem = ({ field, onChange, onDelete }: any) => {
  const { id, label, required } = field;

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={tw`p-4 border rounded-md bg-white mb-2 space-y-2`}
    >
      <div className={tw`flex items-center justify-between`}>
        <input
          type="text"
          value={label}
          onChange={(e) => onChange(id, "label", e.target.value)}
          placeholder="Field Label"
          className={tw`border rounded-md px-2 py-1 w-full focus:outline-none`}
        />
        <div
          {...listeners}
          {...attributes}
          className={tw`ml-2 cursor-grab text-gray-400 text-xl hover:text-gray-600`}
          title="Drag to reorder"
        >
          ☰
        </div>
      </div>

      <div className={tw`flex items-center justify-between`}>
        <div className={tw`flex items-center gap-x-2`}>
          <label className={tw`text-sm`}>Required:</label>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => onChange(id, "required", e.target.checked)}
            className={tw`w-4 h-4`}
          />
        </div>
        <DeleteIcon color={"var(--primary)"} onClick={() => onDelete(id)} />
      </div>
    </div>
  );
};

const FormBuilder = ({ id, setIsOpen }) => {
  const [fields, setFields] = useState<
    { id: string; type: string; label: string; required: boolean }[]
  >([]);

  const { mutateAsync, isLoading } = usePatchHook({
    queryKey: ["subject"],
    navigateURL: "",
  });

  const { data, isLoading: isSubjetDataLoading } = useGetHook({
    queryKey: ["subject", `subject-${id}`],
    url: `${APIS.SUBJECT}${id}/`,
    params: {},
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleAddField = (type: "text" | "char") => {
    const id = `${type}-${Date.now()}`;
    setFields((prev) => [
      ...prev,
      { id, type, label: `${type} field`, required: false },
    ]);
  };

  const handleChange = (id: string, key: "label" | "required", value: any) => {
    setFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, [key]: value } : field
      )
    );
  };

  const handleDelete = (id: string) => {
    setFields((prev) => prev.filter((f) => f.id !== id));
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((f) => f.id === active.id);
      const newIndex = fields.findIndex((f) => f.id === over.id);
      setFields((items) => arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleSubmit = async () => {
    const extra_data = fields;
    if (extra_data?.length === 0) {
      toast.error("Please add at least one field to continue...");
      return;
    }
    const url = `${APIS.SUBJECT}${id}/`;
    const formData = {
      extra_data: extra_data,
    };
    try {
      const res = await mutateAsync({ url, formData, setError: () => {} });
      if (res?.status === 200) {
        setIsOpen((prev) => !prev);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (data && typeof data === "object") {
      setFields(data?.extra_data ?? []);
    }
  }, [data]);

  return isSubjetDataLoading ? (
    <Spinner />
  ) : (
    <div className={tw`p-4 bg-white rounded-md relative`}>
      <div className={tw`grid grid-cols-2 gap-4 mb-4`}>
        <button
          onClick={() => handleAddField("text")}
          className={tw`px-4 py-2 border border-primary text-primary rounded-md focus:outline-none`}
        >
          Add Text Field
        </button>
        <button
          onClick={() => handleAddField("char")}
          className={tw`px-4 py-2 border border-primary text-primary rounded-md focus:outline-none`}
        >
          Add Character Field
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={fields.map((field) => field.id)}
          strategy={verticalListSortingStrategy}
        >
          {fields.map((field) => (
            <SortableItem
              key={field.id}
              field={field}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
      <div className={tw`mr-auto w-24`}>
        <Button
          onClick={handleSubmit}
          label={"Submit Form"}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default FormBuilder;
